import { c as _c } from "react/compiler-runtime";
import { Avatar, Badge, Button, Card, Center, Heading, HStack, Icon, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react';
import { AiAvatarBadge } from '@src/components/sco/Account/UserAvatar';
import { ADVISORS_FEATURES_ENABLED, WIL_AVATAR, WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants';
import { filterEngagements, useAdvisorBoard } from '@src/components/sco/Advisors/hooks/useAdvisorBoard';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useWilMessageUsage } from '@src/components/sco/Advisors/hooks/useWilMessageUsage';
import { LOCKED_TEAM_ADVISORS, useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { hasSeenAdvisorsHomeOrientation, hasSeenDualRoleTip, markAdvisorsHomeOrientationSeen, markDualRoleTipSeen, shouldSkipHomeToWil } from '@src/components/sco/Advisors/utils/advisorsHomeOrientation';
import { acceptingSessionsDirectoryHref } from '@src/components/sco/Advisors/utils/directoryFilters';
import { askWilHref, engagementChatHref, engagementDisplayName, isHumanBoardAdvisor } from '@src/components/sco/Advisors/utils/mapEngagements';
import { sortAdvisorBoardEngagements } from '@src/components/sco/Advisors/utils/sortEngagementsByChatActivity';
import { usePlanPriceLabels } from '@src/components/sco/Conversion/controller/usePlanPriceLabels';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_SOURCE_TEAM } from '@src/constants/advisor-engagement-sources';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { LuArrowRight, LuBriefcase, LuLock, LuSparkles, LuUserPlus, LuVideo } from 'react-icons/lu';
function engagementName(engagement: ScoAdvisorEngagement) {
  return engagementDisplayName(engagement);
}
function engagementAvatar(engagement: ScoAdvisorEngagement) {
  if (engagement.advisorUser?.pictureUrl) {
    return engagement.advisorUser.pictureUrl;
  }
  if (engagement.isAI) {
    return WIL_AVATAR;
  }
  return undefined;
}
function engagementSubtitle(engagement: ScoAdvisorEngagement) {
  if (engagement.isAI) {
    return 'Always on · instant answers';
  }
  const lastActive = engagement.chatThread?.lastActiveAt;
  const unread = engagement.chatThread?.currentUserUnreadCommentsCount ?? 0;
  const parts: string[] = [];
  if (engagement.category?.title) {
    parts.push(engagement.category.title);
  } else if (engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM) {
    parts.push('Startups.com team');
  }
  if (unread > 0) {
    parts.push(`${unread} unread`);
  }
  // else if (lastActive) parts.push(`Active ${moment(lastActive).fromNow()}`)
  return parts.join(' · ') || 'Message your advisor';
}
function canMessageEngagement(engagement: ScoAdvisorEngagement, canAccessAdvisorTeam: boolean) {
  if (engagement.isAI) {
    return true;
  }
  if (engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM) {
    return canAccessAdvisorTeam;
  }
  return true;
}
type OptionCardProps = {
  href: string;
  name: string;
  subtitle: string;
  avatarUrl?: string;
  isAI?: boolean;
  badge?: string;
  badgePalette?: string;
  comingSoon?: boolean;
  ctaLabel: string;
  featured?: boolean;
  locked?: boolean;
  icon?: ReactNode;
  /** Vertical card for the Start here grid. Row layout stays for advisor lists. */
  stacked?: boolean;
  /** Wrap the Message CTA in the $49 PaywalledAction instead of making the whole card a link. */
  paywallMessage?: boolean;
};
export const OptionCard = t0 => {
  const $ = _c(56);
  const {
    href,
    name,
    subtitle,
    avatarUrl,
    isAI,
    badge,
    badgePalette: t1,
    comingSoon,
    ctaLabel,
    featured,
    locked,
    icon,
    stacked,
    paywallMessage
  } = t0;
  const badgePalette = t1 === undefined ? "gray" : t1;
  const t2 = isAI ? "teal" : locked ? "orange" : "gray";
  const t3 = stacked ? "lg" : "md";
  let t4;
  if ($[0] !== icon || $[1] !== isAI || $[2] !== locked || $[3] !== stacked) {
    t4 = icon ? <Icon boxSize={stacked ? 5 : 4}>{icon}</Icon> : isAI ? <Icon boxSize={stacked ? 5 : 4}><LuSparkles /></Icon> : locked ? <Icon boxSize={stacked ? 5 : 4}><LuLock /></Icon> : null;
    $[0] = icon;
    $[1] = isAI;
    $[2] = locked;
    $[3] = stacked;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== name || $[6] !== t4) {
    t5 = <Avatar.Fallback name={name}>{t4}</Avatar.Fallback>;
    $[5] = name;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== avatarUrl) {
    t6 = avatarUrl ? <Avatar.Image src={avatarUrl} /> : null;
    $[8] = avatarUrl;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isAI) {
    t7 = isAI ? <AiAvatarBadge /> : null;
    $[10] = isAI;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <Avatar.Root colorPalette={t2} flexShrink={0} size={t3}>{t5}{t6}{t7}</Avatar.Root>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const avatar = t8;
  let t9;
  if ($[18] !== comingSoon || $[19] !== ctaLabel || $[20] !== featured || $[21] !== href || $[22] !== locked || $[23] !== paywallMessage || $[24] !== stacked) {
    t9 = comingSoon ? <Button disabled={true} size="sm" variant="outline">Coming Soon</Button> : paywallMessage ? <PaywalledAdvisorAction upgradeCtaButtonProps={{
      size: "sm",
      width: stacked ? "full" : undefined,
      flexShrink: 0
    }}><Button asChild={true} flexShrink={0} size="sm" variant={featured ? "cta" : "outline"} width={stacked ? "full" : undefined}><NextLink href={href}>{ctaLabel}<LuArrowRight /></NextLink></Button></PaywalledAdvisorAction> : <Button flexShrink={0} pointerEvents="none" size="sm" variant={featured ? "cta" : locked ? "cta" : "outline"} width={stacked ? "full" : undefined}>{locked ? <LuLock /> : null}{ctaLabel}{!locked ? <LuArrowRight /> : null}</Button>;
    $[18] = comingSoon;
    $[19] = ctaLabel;
    $[20] = featured;
    $[21] = href;
    $[22] = locked;
    $[23] = paywallMessage;
    $[24] = stacked;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const cta = t9;
  let t10;
  if ($[26] !== name) {
    t10 = <Text fontWeight="semibold" lineClamp={1}>{name}</Text>;
    $[26] = name;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== badge || $[29] !== badgePalette) {
    t11 = badge ? <Badge colorPalette={badgePalette} size="sm" variant="subtle">{badge}</Badge> : null;
    $[28] = badge;
    $[29] = badgePalette;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = <HStack gap={2} wrap="wrap">{t10}{t11}</HStack>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const titleRow = t12;
  const t13 = stacked ? "column" : undefined;
  const t14 = stacked ? 4 : undefined;
  const t15 = stacked ? "full" : undefined;
  const t16 = stacked ? 5 : 4;
  const t17 = stacked ? 5 : 4;
  let t18;
  if ($[34] !== avatar || $[35] !== cta || $[36] !== stacked || $[37] !== subtitle || $[38] !== titleRow) {
    t18 = stacked ? <>{avatar}<VStack align="stretch" flex={1} gap={1} minW={0}>{titleRow}<Text color="colorPalette.fg/60" fontSize="sm">{subtitle}</Text></VStack>{cta}</> : <HStack align="center" gap={4}>{avatar}<VStack align="stretch" flex={1} gap={0.5} minW={0}>{titleRow}<Text color="colorPalette.fg/60" fontSize="sm" lineClamp={2}>{subtitle}</Text></VStack>{cta}</HStack>;
    $[34] = avatar;
    $[35] = cta;
    $[36] = stacked;
    $[37] = subtitle;
    $[38] = titleRow;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t13 || $[41] !== t14 || $[42] !== t15 || $[43] !== t16 || $[44] !== t17 || $[45] !== t18) {
    t19 = <Card.Body display="flex" flexDirection={t13} gap={t14} height={t15} paddingX={t16} paddingY={t17}>{t18}</Card.Body>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const body = t19;
  const t20 = !paywallMessage;
  const t21 = locked ? "orange" : featured ? "teal" : undefined;
  const t22 = stacked ? "full" : undefined;
  let t23;
  if ($[47] !== body || $[48] !== href || $[49] !== paywallMessage) {
    t23 = paywallMessage ? body : <NextLink href={href}>{body}</NextLink>;
    $[47] = body;
    $[48] = href;
    $[49] = paywallMessage;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23) {
    t24 = <Card.Root asChild={t20} colorPalette={t21} height={t22} variant="interactive">{t23}</Card.Root>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  return t24;
};
export const AddAdvisorsCard = t0 => {
  const $ = _c(10);
  const {
    directoryHref,
    seatNewHref
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Avatar.Root colorPalette="gray" flexShrink={0} size="md"><Avatar.Fallback><Icon boxSize={4}><LuUserPlus /></Icon></Avatar.Fallback></Avatar.Root>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <VStack align="stretch" flex={1} gap={0.5} minW={0}><Text fontWeight="semibold" lineClamp={1}>Add advisors to your board</Text><Text color="colorPalette.fg/60" fontSize="sm" lineClamp={2}>Recruit seats or invite domain experts to message them here.</Text></VStack>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LuArrowRight />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== directoryHref) {
    t4 = <Button asChild={true} size="sm" variant="cta"><NextLink href={directoryHref}>Find advisors{t3}</NextLink></Button>;
    $[3] = directoryHref;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== seatNewHref) {
    t5 = <Button asChild={true} size="sm" variant="outline"><NextLink href={seatNewHref}>Open a board seat</NextLink></Button>;
    $[5] = seatNewHref;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <Card.Root variant="interactive"><Card.Body paddingX={4} paddingY={4}><HStack align="center" gap={4} wrap="wrap">{t1}{t2}<HStack flexShrink={0} gap={2} wrap="wrap">{t4}{t5}</HStack></HStack></Card.Body></Card.Root>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
export const HomeSection = () => {
  const $ = _c(131);
  const planPrices = usePlanPriceLabels();
  const paths = useAdvisorsPaths();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const {
    canAccessAdvisorTeam,
    teamUpgradeHref,
    isWilMessageLimited,
    isRegistered,
    isLoading
  } = useAdvisorsAccess();
  const wilUsage = useWilMessageUsage();
  const [seenHomeAtMount] = useState(_temp);
  const [showDualTip, setShowDualTip] = useState(false);
  const query = useAdvisorBoard();
  query.data?.advisorBoard;
  let locked;
  let t0;
  let t1;
  let t2;
  if ($[0] !== canAccessAdvisorTeam || $[1] !== query.data?.advisorBoard) {
    const board = filterEngagements(query.data?.advisorBoard);
    const sorted = sortAdvisorBoardEngagements(board);
    const wilEngagement = sorted.find(_temp2) || null;
    const humans = sorted.filter(isHumanBoardAdvisor);
    let t3;
    if ($[6] !== canAccessAdvisorTeam) {
      t3 = e_0 => canMessageEngagement(e_0, canAccessAdvisorTeam);
      $[6] = canAccessAdvisorTeam;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const messageable = humans.filter(t3);
    let t4;
    if ($[8] !== canAccessAdvisorTeam) {
      t4 = e_1 => !canMessageEngagement(e_1, canAccessAdvisorTeam);
      $[8] = canAccessAdvisorTeam;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    locked = humans.filter(t4);
    const isTeam = _temp3;
    t0 = wilEngagement;
    t1 = messageable.filter(isTeam);
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = engagement_0 => !isTeam(engagement_0);
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t2 = messageable.filter(t5);
    $[0] = canAccessAdvisorTeam;
    $[1] = query.data?.advisorBoard;
    $[2] = locked;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    locked = $[2];
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[11] !== locked || $[12] !== t0 || $[13] !== t1 || $[14] !== t2) {
    t3 = {
      wil: t0,
      messageableTeam: t1,
      messageableBoard: t2,
      lockedTeamAdvisors: locked
    };
    $[11] = locked;
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const {
    wil,
    messageableTeam,
    messageableBoard,
    lockedTeamAdvisors
  } = t3;
  const hasUsedWil = isWilMessageLimited && wilUsage.usedMessages > 0;
  let t4;
  if ($[16] !== hasUsedWil || $[17] !== isLoading || $[18] !== isRegistered || $[19] !== isWilMessageLimited || $[20] !== seenHomeAtMount) {
    t4 = shouldSkipHomeToWil({
      isRegistered,
      isLoading,
      isWilMessageLimited,
      hasSeenOrientation: seenHomeAtMount,
      hasUsedWil
    });
    $[16] = hasUsedWil;
    $[17] = isLoading;
    $[18] = isRegistered;
    $[19] = isWilMessageLimited;
    $[20] = seenHomeAtMount;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  const skipHomeToWil = t4;
  let t5;
  let t6;
  if ($[22] !== paths.ask || $[23] !== router || $[24] !== skipHomeToWil) {
    t5 = () => {
      if (!skipHomeToWil) {
        return;
      }
      router.replace(paths.ask);
    };
    t6 = [skipHomeToWil, paths.ask, router];
    $[22] = paths.ask;
    $[23] = router;
    $[24] = skipHomeToWil;
    $[25] = t5;
    $[26] = t6;
  } else {
    t5 = $[25];
    t6 = $[26];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[27] !== isLoading || $[28] !== skipHomeToWil) {
    t7 = () => {
      if (isLoading || skipHomeToWil) {
        return;
      }
      markAdvisorsHomeOrientationSeen();
    };
    t8 = [isLoading, skipHomeToWil];
    $[27] = isLoading;
    $[28] = skipHomeToWil;
    $[29] = t7;
    $[30] = t8;
  } else {
    t7 = $[29];
    t8 = $[30];
  }
  useEffect(t7, t8);
  let t9;
  if ($[31] !== currentUser?.advisorProfile?.isApproved) {
    t9 = () => {
      if (currentUser?.advisorProfile?.isApproved && !hasSeenDualRoleTip()) {
        setShowDualTip(true);
      }
    };
    $[31] = currentUser?.advisorProfile?.isApproved;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const t10 = currentUser?.advisorProfile?.isApproved;
  let t11;
  if ($[33] !== t10) {
    t11 = [t10];
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  useEffect(t9, t11);
  let t12;
  bb0: {
    if (!isWilMessageLimited) {
      let t13;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {
          label: "Unlimited",
          palette: "green" as const
        };
        $[35] = t13;
      } else {
        t13 = $[35];
      }
      t12 = t13;
      break bb0;
    }
    if (wilUsage.isLimitReached) {
      let t13;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {
          label: "Limit reached",
          palette: "orange" as const
        };
        $[36] = t13;
      } else {
        t13 = $[36];
      }
      t12 = t13;
      break bb0;
    }
    if (wilUsage.remainingMessages <= 10) {
      const t13 = `${wilUsage.remainingMessages} left`;
      let t14;
      if ($[37] !== t13) {
        t14 = {
          label: t13,
          palette: "orange" as const
        };
        $[37] = t13;
        $[38] = t14;
      } else {
        t14 = $[38];
      }
      t12 = t14;
      break bb0;
    }
    let t13;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = {
        label: "Free",
        palette: "green" as const
      };
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    t12 = t13;
  }
  const wilBadge = t12;
  let t13;
  bb1: {
    if (wilUsage.isLimitReached) {
      t13 = "Free messages used up \u2014 upgrade for unlimited chat";
      break bb1;
    }
    if (isWilMessageLimited && wilUsage.remainingMessages < wilUsage.limit) {
      t13 = `${wilUsage.remainingMessages} free messages left · upgrade for unlimited`;
      break bb1;
    }
    if (wil) {
      let t14;
      if ($[40] !== wil) {
        t14 = engagementSubtitle(wil);
        $[40] = wil;
        $[41] = t14;
      } else {
        t14 = $[41];
      }
      t13 = t14;
      break bb1;
    }
    t13 = "Always on \xB7 free to start";
  }
  const wilSubtitle = t13;
  let t14;
  if ($[42] !== paths.base) {
    t14 = advisorsBreadcrumbsWithTrail(paths.base, {
      title: "Home",
      href: paths.base
    });
    $[42] = paths.base;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t14) {
    t15 = {
      breadcrumbs: t14
    };
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  useAdvisorsToolbar(t15);
  const showLockedTeamPreview = !canAccessAdvisorTeam && lockedTeamAdvisors.length === 0;
  if (skipHomeToWil || isLoading) {
    let t16;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <Center flex={1} minHeight="40vh" width="full"><Spinner colorPalette="orange" size="lg" /></Center>;
      $[46] = t16;
    } else {
      t16 = $[46];
    }
    return t16;
  }
  const t16 = query.error;
  const t17 = query.isInitialLoading;
  let t18;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      base: 2,
      md: 4
    };
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Text color="orange.fg" textStyle="labelXs">Advisors</Text>;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <VStack align="stretch" gap={1} textAlign="left">{t19}<Heading size={{
        base: "xl",
        md: "2xl"
      }}>What can we get done today?</Heading></VStack>;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== showDualTip) {
    t21 = showDualTip ? <Card.Root size="sm" variant="subtle"><Card.Body><HStack align="start" gap={3} justify="space-between" wrap="wrap"><Text color="colorPalette.fg/80" fontSize="sm">You can advise founders and still recruit your own board. This is one identity — not a mode switch.</Text><Button size="xs" variant="ghost" onClick={() => {
            markDualRoleTipSeen();
            setShowDualTip(false);
          }}>Got it</Button></HStack></Card.Body></Card.Root> : null;
    $[50] = showDualTip;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== getBreakpointValue) {
    t22 = getBreakpointValue([1, 1, 3]) ?? 1;
    $[52] = getBreakpointValue;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== wil) {
    t23 = wil ? engagementAvatar(wil) || WIL_AVATAR : WIL_AVATAR;
    $[54] = wil;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  const t24 = wilUsage.isLimitReached ? "Upgrade" : "Ask Wil.ai";
  let t25;
  if ($[56] !== paths.base || $[57] !== wil) {
    t25 = wil ? engagementChatHref(wil, paths.base) : askWilHref(paths.base);
    $[56] = paths.base;
    $[57] = wil;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== wil) {
    t26 = wil ? engagementName(wil) : WIL_DISPLAY_NAME;
    $[59] = wil;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t23 || $[62] !== t24 || $[63] !== t25 || $[64] !== t26 || $[65] !== wilBadge.label || $[66] !== wilBadge.palette || $[67] !== wilSubtitle || $[68] !== wilUsage.isLimitReached) {
    t27 = <OptionCard featured={true} isAI={true} stacked={true} avatarUrl={t23} badge={wilBadge.label} badgePalette={wilBadge.palette} ctaLabel={t24} href={t25} locked={wilUsage.isLimitReached} name={t26} subtitle={wilSubtitle} />;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
    $[64] = t26;
    $[65] = wilBadge.label;
    $[66] = wilBadge.palette;
    $[67] = wilSubtitle;
    $[68] = wilUsage.isLimitReached;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <LuBriefcase />;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== paths.directory) {
    t29 = <OptionCard stacked={true} comingSoon={!ADVISORS_FEATURES_ENABLED} ctaLabel="Find advisors" href={paths.directory} icon={t28} name="Recruit a board" subtitle={"Invite experts onto an open seat \u2014 or create one first."} />;
    $[71] = paths.directory;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== paths) {
    t30 = acceptingSessionsDirectoryHref(paths);
    $[73] = paths;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <LuVideo />;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  let t32;
  if ($[76] !== t30) {
    t32 = <OptionCard stacked={true} comingSoon={!ADVISORS_FEATURES_ENABLED} ctaLabel="Book a Session" href={t30} icon={t31} name="Book a paid advisor session" subtitle="Video call with advisors who take sessions." />;
    $[76] = t30;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== t22 || $[79] !== t27 || $[80] !== t29 || $[81] !== t32) {
    t33 = <VStack align="stretch" gap={3}><SimpleGrid columns={t22} gap={4} width="full">{t27}{t29}{t32}</SimpleGrid></VStack>;
    $[78] = t22;
    $[79] = t27;
    $[80] = t29;
    $[81] = t32;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Text color="colorPalette.fg/50" textAlign="left" textStyle="labelXs">Your advisors</Text>;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== paths.directory || $[85] !== paths.seatNew) {
    t35 = ADVISORS_FEATURES_ENABLED && <AddAdvisorsCard directoryHref={paths.directory} seatNewHref={paths.seatNew} />;
    $[84] = paths.directory;
    $[85] = paths.seatNew;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== messageableTeam || $[88] !== paths.base) {
    let t37;
    if ($[90] !== paths.base) {
      t37 = engagement_1 => {
        const name = engagementName(engagement_1);
        const unread = engagement_1.chatThread?.currentUserUnreadCommentsCount ?? 0;
        return <OptionCard key={engagement_1.advisorEngagementId || engagement_1.advisorUserId || name} paywallMessage={true} avatarUrl={engagementAvatar(engagement_1)} badge={unread > 0 ? `${unread} new` : undefined} badgePalette={unread > 0 ? "orange" : "gray"} ctaLabel="Message" href={engagementChatHref(engagement_1, paths.base)} name={name} subtitle={engagementSubtitle(engagement_1)} />;
      };
      $[90] = paths.base;
      $[91] = t37;
    } else {
      t37 = $[91];
    }
    t36 = messageableTeam.map(t37);
    $[87] = messageableTeam;
    $[88] = paths.base;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[92] !== lockedTeamAdvisors || $[93] !== paths.base || $[94] !== planPrices || $[95] !== teamUpgradeHref) {
    let t38;
    if ($[97] !== paths.base || $[98] !== planPrices || $[99] !== teamUpgradeHref) {
      t38 = engagement_2 => {
        const name_0 = engagementName(engagement_2);
        return <OptionCard key={engagement_2.advisorEngagementId || engagement_2.advisorUserId || name_0} locked={true} avatarUrl={engagementAvatar(engagement_2)} badge={planPrices.team || "Advisor Team"} badgePalette="orange" ctaLabel="Unlock" href={teamUpgradeHref || `${paths.base}/upgrade`} name={name_0} subtitle={"Startups.com team \xB7 Upgrade to message"} />;
      };
      $[97] = paths.base;
      $[98] = planPrices;
      $[99] = teamUpgradeHref;
      $[100] = t38;
    } else {
      t38 = $[100];
    }
    t37 = lockedTeamAdvisors.map(t38);
    $[92] = lockedTeamAdvisors;
    $[93] = paths.base;
    $[94] = planPrices;
    $[95] = teamUpgradeHref;
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  let t38;
  if ($[101] !== paths.base || $[102] !== planPrices || $[103] !== showLockedTeamPreview || $[104] !== teamUpgradeHref) {
    t38 = showLockedTeamPreview ? LOCKED_TEAM_ADVISORS.map(person => <OptionCard key={person.name} locked={true} avatarUrl={person.avatarUrl} badge={planPrices.team || "Advisor Team"} badgePalette="orange" ctaLabel="Unlock" href={teamUpgradeHref || `${paths.base}/upgrade`} name={person.name} subtitle={`${person.role} · Upgrade to message`} />) : null;
    $[101] = paths.base;
    $[102] = planPrices;
    $[103] = showLockedTeamPreview;
    $[104] = teamUpgradeHref;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  let t39;
  if ($[106] !== messageableBoard || $[107] !== paths.base) {
    let t40;
    if ($[109] !== paths.base) {
      t40 = engagement_3 => {
        const name_1 = engagementName(engagement_3);
        const unread_0 = engagement_3.chatThread?.currentUserUnreadCommentsCount ?? 0;
        return <OptionCard key={engagement_3.advisorEngagementId || engagement_3.advisorUserId || name_1} paywallMessage={true} avatarUrl={engagementAvatar(engagement_3)} badge={unread_0 > 0 ? `${unread_0} new` : undefined} badgePalette={unread_0 > 0 ? "orange" : "gray"} ctaLabel="Message" href={engagementChatHref(engagement_3, paths.base)} name={name_1} subtitle={engagementSubtitle(engagement_3)} />;
      };
      $[109] = paths.base;
      $[110] = t40;
    } else {
      t40 = $[110];
    }
    t39 = messageableBoard.map(t40);
    $[106] = messageableBoard;
    $[107] = paths.base;
    $[108] = t39;
  } else {
    t39 = $[108];
  }
  let t40;
  if ($[111] !== canAccessAdvisorTeam || $[112] !== messageableBoard.length || $[113] !== messageableTeam.length || $[114] !== paths.inbox) {
    t40 = messageableTeam.length + messageableBoard.length > 0 || !canAccessAdvisorTeam ? <Button asChild={true} alignSelf="center" size="sm" variant="ghost"><NextLink href={paths.inbox}>Open Inbox<LuArrowRight /></NextLink></Button> : null;
    $[111] = canAccessAdvisorTeam;
    $[112] = messageableBoard.length;
    $[113] = messageableTeam.length;
    $[114] = paths.inbox;
    $[115] = t40;
  } else {
    t40 = $[115];
  }
  let t41;
  if ($[116] !== t35 || $[117] !== t36 || $[118] !== t37 || $[119] !== t38 || $[120] !== t39 || $[121] !== t40) {
    t41 = <VStack align="stretch" gap={2}>{t34}{t35}{t36}{t37}{t38}{t39}{t40}</VStack>;
    $[116] = t35;
    $[117] = t36;
    $[118] = t37;
    $[119] = t38;
    $[120] = t39;
    $[121] = t40;
    $[122] = t41;
  } else {
    t41 = $[122];
  }
  let t42;
  if ($[123] !== t21 || $[124] !== t33 || $[125] !== t41) {
    t42 = <SectionScroll maxWidth="5xl"><VStack align="stretch" gap={6} paddingTop={t18}>{t20}{t21}{t33}{t41}</VStack></SectionScroll>;
    $[123] = t21;
    $[124] = t33;
    $[125] = t41;
    $[126] = t42;
  } else {
    t42 = $[126];
  }
  let t43;
  if ($[127] !== query.error || $[128] !== query.isInitialLoading || $[129] !== t42) {
    t43 = <AdvisorsQueryState error={t16} loading={t17}>{t42}</AdvisorsQueryState>;
    $[127] = query.error;
    $[128] = query.isInitialLoading;
    $[129] = t42;
    $[130] = t43;
  } else {
    t43 = $[130];
  }
  return t43;
};
function _temp() {
  return hasSeenAdvisorsHomeOrientation();
}
function _temp2(e) {
  return e.isAI;
}
function _temp3(engagement) {
  return engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM;
}